/**
 * VÉRAULT Portal — NewJourneyModal
 *
 * The "Begin a journey" entry point. Quiet luxury form for spinning up a
 * trip in a single sweep, then deep-linking into the JourneyDetail builder
 * (Slices A+B already in place).
 *
 *  - Picks an existing client (typeahead by name/email) — required.
 *  - Captures destinations, start/end dates, num_travelers, trip_type.
 *  - POSTs /api/travels/trips → navigates to /portal/advisor/journeys/:id.
 *
 * Triggered from Atelier "Quick rooms" and the Trips list page header.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Search, Plane, Sparkles, Bookmark, UserPlus, Compass } from "lucide-react";
import { api, PortalButton } from "../index";
import { toast } from "sonner";
import NewClientModal from "./NewClientModal";

const TRIP_TYPES = [
  { value: "solo", label: "Solo" },
  { value: "couple", label: "Couple" },
  { value: "family", label: "Family" },
  { value: "group", label: "Group" },
  { value: "business", label: "Business" },
];

const inputClass =
  "w-full px-3 py-2 text-[14px] bg-[hsl(var(--background-paper))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.12)] focus:bg-white transition-colors placeholder:text-[hsl(var(--foreground-muted))]";

const labelClass =
  "font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5 block";

function Field({ label, children, span = 1, hint }) {
  return (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
      <span className={labelClass}>{label}</span>
      {children}
      {hint && (
        <p className="mt-1 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">{hint}</p>
      )}
    </div>
  );
}

export default function NewJourneyModal({ open, onClose, initialClientId = null }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(initialClientId || "");
  const [clientSearch, setClientSearch] = useState("");
  const [showClientList, setShowClientList] = useState(false);

  const [destinations, setDestinations] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [numTravelers, setNumTravelers] = useState(1);
  const [tripType, setTripType] = useState("couple");
  const [tripSummary, setTripSummary] = useState("");
  const [newClientOpen, setNewClientOpen] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [loadedClients, setLoadedClients] = useState(false);
  const [mode, setMode] = useState("blank"); // "blank" | "template" | "trip-template"
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [tripTemplates, setTripTemplates] = useState([]);
  const [tripTemplateId, setTripTemplateId] = useState("");
  const [loadingTripTemplates, setLoadingTripTemplates] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoadingTemplates(true);
    api
      .get("/journey-templates")
      .then(({ data }) => setTemplates(Array.isArray(data) ? data : []))
      .catch(() => setTemplates([]))
      .finally(() => setLoadingTemplates(false));
    setLoadingTripTemplates(true);
    api
      .get("/planning-studio/trip-templates")
      .then(({ data }) => setTripTemplates(Array.isArray(data?.trip_templates) ? data.trip_templates : []))
      .catch(() => setTripTemplates([]))
      .finally(() => setLoadingTripTemplates(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    api
      .get("/travels/clients")
      .then(({ data }) => {
        if (!alive) return;
        setClients(Array.isArray(data) ? data : []);
        setLoadedClients(true);
        if (initialClientId) {
          const c = (data || []).find((x) => x.id === initialClientId);
          if (c) setClientSearch(c.full_name || c.email || "");
        }
      })
      .catch(() => alive && setLoadedClients(true));
    return () => {
      alive = false;
    };
  }, [open, initialClientId]);

  useEffect(() => {
    if (!open) {
      // reset everything when closed
      setClientId(initialClientId || "");
      setClientSearch("");
      setShowClientList(false);
      setDestinations("");
      setStartDate("");
      setEndDate("");
      setNumTravelers(1);
      setTripType("couple");
      setTripSummary("");
      setSubmitting(false);
      setMode("blank");
      setTemplateId("");
      setTripTemplateId("");
    }
  }, [open, initialClientId]);

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients.slice(0, 6);
    return clients
      .filter((c) =>
        [c.full_name, c.email, c.phone, c.household_label]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(q))
      )
      .slice(0, 8);
  }, [clients, clientSearch]);

  const selectedClient = clients.find((c) => c.id === clientId);

  const canSubmit =
    !!clientId &&
    !submitting &&
    !!destinations.trim() &&
    !!startDate &&
    !!endDate &&
    new Date(endDate) >= new Date(startDate) &&
    (mode === "template"
      ? !!templateId
      : mode === "trip-template"
      ? !!tripTemplateId
      : true);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (mode === "template") {
        const { data } = await api.post(`/journey-templates/${templateId}/instantiate`, {
          client_id: clientId,
          trip_start_date: startDate || undefined,
          trip_end_date: endDate || undefined,
          internal_trip_name: destinations.trim() || undefined,
        });
        if (data?.id) {
          toast.success("Experience created from template");
          onClose?.();
          navigate(`/portal/advisor/journeys/${data.id}`);
        } else {
          toast.error("Couldn't create experience from template.");
        }
      } else if (mode === "trip-template") {
        // 1. Create the blank trip with dates/destinations.
        const payload = {
          client_id: clientId,
          destinations: destinations.trim(),
          start_date: startDate,
          end_date: endDate,
          num_travelers: Number(numTravelers) || 1,
          trip_type: tripType,
          trip_summary: tripSummary.trim() || undefined,
        };
        const { data: trip } = await api.post("/travels/trips", payload);
        if (!trip?.id) {
          toast.error("Couldn't create experience — no id returned.");
          return;
        }
        // 2. Apply the selected Planning Studio Trip Template.
        try {
          const { data: cloneRes } = await api.post(
            `/planning-studio/trip-templates/${tripTemplateId}/clone-to-trip`,
            { trip_id: trip.id, start_day_number: 1, overwrite: true }
          );
          const applied = cloneRes?.days_applied || 0;
          const skipped = cloneRes?.days_skipped || 0;
          toast.success(
            `Experience created · template applied to ${applied} day${applied === 1 ? "" : "s"}` +
              (skipped ? ` · ${skipped} template day${skipped === 1 ? "" : "s"} didn't fit` : "")
          );
        } catch (cloneErr) {
          // Trip is created; surface a calm warning so the advisor knows.
          toast.warning(
            "Experience created — but the trip template couldn't be applied. You can clone it from Planning Studio."
          );
        }
        onClose?.();
        navigate(`/portal/advisor/journeys/${trip.id}`);
      } else {
        const payload = {
          client_id: clientId,
          destinations: destinations.trim(),
          start_date: startDate,
          end_date: endDate,
          num_travelers: Number(numTravelers) || 1,
          trip_type: tripType,
          trip_summary: tripSummary.trim() || undefined,
        };
        const { data } = await api.post("/travels/trips", payload);
        if (data?.id) {
          toast.success("Experience created");
          onClose?.();
          navigate(`/portal/advisor/journeys/${data.id}`);
        } else {
          toast.error("Couldn't create experience — no id returned.");
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Couldn't create experience.";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          data-testid="new-journey-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.22, 0.61, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-[hsl(var(--background))] rounded-xl shadow-floating max-h-[90vh] overflow-y-auto"
          >
            <div className="relative px-8 pt-8 pb-2">
              <button
                onClick={onClose}
                className="absolute top-5 right-5 size-8 rounded-full flex items-center justify-center hover:bg-[hsl(var(--background-paper))] transition-colors"
                data-testid="new-journey-close-btn"
                aria-label="Close"
              >
                <X className="size-4" strokeWidth={1.5} />
              </button>
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                Begin an experience
              </p>
              <h2 className="mt-2 font-serif text-3xl text-[hsl(var(--foreground))]">
                A new itinerary takes shape.
              </h2>
              <p className="mt-2 font-accent italic text-[14px] text-[hsl(var(--foreground-secondary))]">
                A handful of details — the rest unfolds in the studio.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="px-8 pb-8 pt-4 space-y-6">
              {/* Mode toggle: Blank vs From template */}
              <div className="flex items-center gap-1.5 border-b border-[rgba(0,0,0,0.06)] -mb-2 flex-wrap">
                {[
                  { id: "blank", label: "Blank experience", icon: Plane },
                  { id: "template", label: `Legacy template${templates.length ? ` · ${templates.length}` : ""}`, icon: Bookmark },
                  { id: "trip-template", label: `Trip Template${tripTemplates.length ? ` · ${tripTemplates.length}` : ""}`, icon: Compass },
                ].map((m) => {
                  const active = mode === m.id;
                  const Icon = m.icon;
                  const disabled =
                    (m.id === "template" && templates.length === 0 && !loadingTemplates) ||
                    (m.id === "trip-template" && tripTemplates.length === 0 && !loadingTripTemplates);
                  return (
                    <button
                      type="button"
                      key={m.id}
                      onClick={() => !disabled && setMode(m.id)}
                      disabled={disabled}
                      data-testid={`new-journey-mode-${m.id}`}
                      className={`px-3 py-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors border-b-2 -mb-px disabled:opacity-40 disabled:cursor-not-allowed ${
                        active
                          ? "text-[hsl(var(--foreground))] border-[hsl(var(--accent-gold))]"
                          : "text-[hsl(var(--foreground-muted))] border-transparent hover:text-[hsl(var(--foreground))]"
                      }`}
                    >
                      <Icon className="size-3" strokeWidth={1.5} />
                      {m.label}
                    </button>
                  );
                })}
              </div>

              {/* Client picker */}
              <Field label="Client">
                <div className="relative">
                  <div className="flex items-center gap-2 bg-[hsl(var(--background-paper))] rounded-md px-3 h-10 border border-transparent focus-within:border-[rgba(0,0,0,0.12)] focus-within:bg-white">
                    <Search className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
                    <input
                      data-testid="new-journey-client-search"
                      type="text"
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        setShowClientList(true);
                        setClientId("");
                      }}
                      onFocus={() => setShowClientList(true)}
                      placeholder={
                        loadedClients
                          ? clients.length
                            ? "Search by name or email…"
                            : "No clients yet — add one in Clients first."
                          : "Loading clients…"
                      }
                      className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-[hsl(var(--foreground-muted))]"
                    />
                    {selectedClient && (
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--accent-gold))]">
                        Selected
                      </span>
                    )}
                  </div>
                  {showClientList && filteredClients.length > 0 && (
                    <div
                      className="absolute z-10 mt-1 left-0 right-0 bg-white rounded-md shadow-floating border border-[rgba(0,0,0,0.06)] overflow-hidden max-h-60 overflow-y-auto"
                      data-testid="new-journey-client-list"
                    >
                      {filteredClients.map((c, i) => (
                        <button
                          type="button"
                          key={`${c.id || "c"}-${i}`}
                          onClick={() => {
                            setClientId(c.id);
                            setClientSearch(c.full_name || c.email || "");
                            setShowClientList(false);
                          }}
                          data-testid={`new-journey-client-option-${c.id}`}
                          className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--background-paper))] transition-colors flex items-center justify-between gap-3"
                        >
                          <div className="min-w-0">
                            <p className="text-[14px] text-[hsl(var(--foreground))] truncate">
                              {c.full_name || "—"}
                            </p>
                            <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] truncate">
                              {c.email || c.household_label || "—"}
                            </p>
                          </div>
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          setShowClientList(false);
                          setNewClientOpen(true);
                        }}
                        data-testid="new-journey-create-client"
                        className="w-full text-left px-3 py-2.5 border-t border-[rgba(0,0,0,0.05)] bg-[hsl(var(--background-paper))]/50 hover:bg-[hsl(var(--background-paper))] transition-colors flex items-center gap-2"
                      >
                        <UserPlus className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                        <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                          + Create new client
                        </span>
                      </button>
                    </div>
                  )}
                  {showClientList && filteredClients.length === 0 && (
                    <div
                      className="absolute z-10 mt-1 left-0 right-0 bg-white rounded-md shadow-floating border border-[rgba(0,0,0,0.06)] overflow-hidden"
                      data-testid="new-journey-client-list-empty"
                    >
                      <div className="px-3 py-3 font-accent italic text-[12.5px] text-[hsl(var(--foreground-secondary))]">
                        No matches for "{clientSearch}".
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setShowClientList(false);
                          setNewClientOpen(true);
                        }}
                        data-testid="new-journey-create-client-empty"
                        className="w-full text-left px-3 py-2.5 border-t border-[rgba(0,0,0,0.05)] bg-[hsl(var(--background-paper))]/50 hover:bg-[hsl(var(--background-paper))] transition-colors flex items-center gap-2"
                      >
                        <UserPlus className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                        <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--accent-gold))]">
                          + Create "{clientSearch}" as new client
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Destinations" hint={mode === "template" ? "Optional — overrides the template's working name." : mode === "trip-template" ? "Where this trip is going — the template provides the day-by-day structure." : "e.g., Tuscany, Italy · or Cape Town & Kruger"}>
                <input
                  data-testid="new-journey-destinations"
                  type="text"
                  value={destinations}
                  onChange={(e) => setDestinations(e.target.value)}
                  placeholder={mode === "template" ? "Optional working name…" : "Where to?"}
                  className={inputClass}
                />
              </Field>

              {mode === "template" && (
                <Field label="Template" hint="Bookings, itinerary blocks, and agreement scaffold come with.">
                  {loadingTemplates ? (
                    <div className="flex items-center gap-2 px-3 py-2 text-[14px] text-[hsl(var(--foreground-muted))]">
                      <Loader2 className="size-3.5 animate-spin" />
                      Loading templates…
                    </div>
                  ) : templates.length === 0 ? (
                    <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] px-1 py-2">
                      No templates yet. Save one from a journey's header to seed this list.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1" data-testid="new-journey-template-list">
                      {templates.map((t, i) => (
                        <button
                          type="button"
                          key={`${t.id || "t"}-${i}`}
                          onClick={() => setTemplateId(t.id)}
                          data-testid={`new-journey-template-${t.id}`}
                          className={`w-full text-left px-4 py-3 rounded-md border transition-all ${
                            templateId === t.id
                              ? "bg-[hsl(var(--accent-gold))]/10 border-[hsl(var(--accent-gold))]/40"
                              : "bg-[hsl(var(--background-paper))]/60 border-transparent hover:bg-[hsl(var(--background-paper))]"
                          }`}
                        >
                          <p className="font-body text-[14px]">{t.name}</p>
                          {t.description && (
                            <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5 line-clamp-2">
                              {t.description}
                            </p>
                          )}
                          <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                            {(t.bookings_scaffold?.length || 0)} bookings · {(t.itinerary_scaffold?.length || 0)} days · {(t.agreements_scaffold?.length || 0)} agreements
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </Field>
              )}

              {mode === "trip-template" && (
                <Field label="Trip Template" hint="A multi-day framework cloned from Planning Studio. You can personalize every detail afterwards.">
                  {loadingTripTemplates ? (
                    <div className="flex items-center gap-2 px-3 py-2 text-[14px] text-[hsl(var(--foreground-muted))]">
                      <Loader2 className="size-3.5 animate-spin" />
                      Loading trip templates…
                    </div>
                  ) : tripTemplates.length === 0 ? (
                    <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))] px-1 py-2">
                      No trip templates yet. Save one from a trip's Executive Office, or ask another advisor to share theirs with the agency.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="new-journey-trip-template-list">
                      {tripTemplates.map((t, i) => (
                        <button
                          type="button"
                          key={`${t.id || "tt"}-${i}`}
                          onClick={() => setTripTemplateId(t.id)}
                          data-testid={`new-journey-trip-template-${t.id}`}
                          className={`w-full text-left px-4 py-3 rounded-md border transition-all ${
                            tripTemplateId === t.id
                              ? "bg-[hsl(var(--accent-gold))]/10 border-[hsl(var(--accent-gold))]/40"
                              : "bg-[hsl(var(--background-paper))]/60 border-transparent hover:bg-[hsl(var(--background-paper))]"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <p className="font-body text-[14px]">{t.name}</p>
                            {!t.is_owner && t.owner_name && (
                              <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[hsl(var(--accent-gold))]/12 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/30">
                                Agency · {t.owner_name}
                              </span>
                            )}
                          </div>
                          {t.description && (
                            <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5 line-clamp-2">
                              {t.description}
                            </p>
                          )}
                          <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                            {t.destination || "unfiled"} · {t.length_in_days || (t.days || []).length} days · used {t.usage_count || 0}×
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </Field>
              )}

              <div className="grid grid-cols-2 gap-4">
                <Field label="Start date">
                  <input
                    data-testid="new-journey-start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={inputClass}
                  />
                </Field>
                <Field label="End date">
                  <input
                    data-testid="new-journey-end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || undefined}
                    className={inputClass}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Travellers">
                  <input
                    data-testid="new-journey-num-travelers"
                    type="number"
                    min={1}
                    max={50}
                    value={numTravelers}
                    onChange={(e) => setNumTravelers(e.target.value)}
                    className={inputClass}
                  />
                </Field>
                <Field label="Trip type">
                  <select
                    data-testid="new-journey-trip-type"
                    value={tripType}
                    onChange={(e) => setTripType(e.target.value)}
                    className={inputClass}
                  >
                    {TRIP_TYPES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="A short summary (optional)" hint="One line that captures the intent.">
                <textarea
                  data-testid="new-journey-summary"
                  value={tripSummary}
                  onChange={(e) => setTripSummary(e.target.value)}
                  placeholder="A slow honeymoon along the Amalfi coast…"
                  rows={2}
                  className={`${inputClass} resize-none`}
                />
              </Field>

              <div className="flex items-center justify-between gap-3 pt-2 border-t border-[rgba(0,0,0,0.05)]">
                <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] flex items-center gap-1.5">
                  <Sparkles className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                  Itinerary days generate automatically once the experience is open.
                </p>
                <div className="flex items-center gap-2">
                  <PortalButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    data-testid="new-journey-cancel-btn"
                  >
                    Cancel
                  </PortalButton>
                  <PortalButton
                    type="submit"
                    variant="gold"
                    size="sm"
                    disabled={!canSubmit}
                    icon={submitting ? <Loader2 className="size-3.5 animate-spin" /> : <Plane className="size-3.5" strokeWidth={1.5} />}
                    data-testid="new-journey-submit-btn"
                  >
                    {submitting ? "Creating…" : "Begin experience"}
                  </PortalButton>
                </div>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      {/* Inline "Create new client" — selects the new client into the experience form. */}
      <NewClientModal
        open={newClientOpen}
        onClose={() => setNewClientOpen(false)}
        navigateToProfile={false}
        onCreated={(c) => {
          setNewClientOpen(false);
          setClientId(c.id);
          setClientSearch(c.full_name || c.email || "");
          setShowClientList(false);
          // Refresh the in-modal client list so the new option appears too
          api.get("/travels/clients").then(({ data }) => setClients(Array.isArray(data) ? data : []));
          toast.success(`${c.full_name || "Client"} ready — continue building the experience.`);
        }}
      />
    </AnimatePresence>
  );
}
